'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter, usePathname } from '@/i18n/routing';
import { getPageKeyFromSlug, getLocalizedSlug, type LocaleCode } from '@/lib/localized-slugs';
const locales = [{
  code: 'en',
  flag: '🇬🇧',
  name: 'English'
}, {
  code: 'fr',
  flag: '🇫🇷',
  name: 'Français'
}, {
  code: 'nl',
  flag: '🇳🇱',
  name: 'Nederlands'
}, {
  code: 'es',
  flag: '🇪🇸',
  name: 'Español'
}, {
  code: 'it',
  flag: '🇮🇹',
  name: 'Italiano'
}];
export default function LocaleSwitcherSubtle({
  currentLocale = 'en'
}: {
  currentLocale?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const currentLocaleData = locales.find(l => l.code === currentLocale) || locales[0];
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleLocaleChange = (localeCode: string) => {
    setIsOpen(false);

    // Check if we're on a custom slug page
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean); // Remove empty strings

    // Handle homepage - if only path part is a locale, go to new locale homepage
    const localesList = ['en', 'fr', 'nl', 'es', 'it'];
    if (pathParts.length === 1 && localesList.includes(pathParts[0])) {
      window.location.href = `/${localeCode}`;
      return;
    }
    const potentialSlug = pathParts[pathParts.length - 1];

    // Find if current slug matches any localized slug using the centralized helper
    const pageKey = getPageKeyFromSlug(potentialSlug);

    // If we found a match, navigate to the localized version
    if (pageKey) {
      const newSlug = getLocalizedSlug(pageKey, localeCode as LocaleCode);
      if (newSlug) {
        window.location.href = `/${localeCode}/${newSlug}`;
        return;
      }
    }

    // Use the pathname from next-intl's usePathname (which is locale-agnostic)
    // and router.push with the new locale
    router.push(pathname as never, {
      locale: localeCode as LocaleCode
    });
  };
  return <div className="relative" ref={dropdownRef}>
      <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors flex items-center gap-1" aria-label="Change language" title={`Language: ${currentLocaleData.name}`}>
        <span className="text-base">{currentLocaleData.flag}</span>
      </button>

      {isOpen && <div className="absolute right-0 mt-2 w-40 rounded-lg border border-primary/20 bg-white shadow-lg overflow-hidden z-50">
          {locales.map(locale => <button key={locale.code} onClick={() => handleLocaleChange(locale.code)} className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-primary/10 transition-colors text-sm ${locale.code === currentLocale ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground'}`}>
              <span className="text-lg">{locale.flag}</span>
              <span className="font-medium">{locale.name}</span>
            </button>)}
        </div>}
    </div>;
}